import type { InteractiveCanvasComponents } from '@mentimeter/question-modules-types';
import { useMemo } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import type { TextResult } from '@core-api/presentation/types/response';
import { TitleSection } from '../../shared/components/title-section/TitleSection';
import { ScreenReaderResults } from './presentation/ScreenReaderResults/ScreenReaderResults';
import { WordCloudVisualization } from './presentation/WordCloudVisualization';

interface WordCloudPresentationProps {
  title: string;
  styledTitle: Record<string, unknown>;
  label: string;
  longerDescription: string;
  fontSizeOffset: -2 | -1 | 0 | 1 | 2;
  shouldHideResults: boolean;
  onRemoveWord: (key: string) => void;
  isModerationEnabled: boolean;
  results: TextResult[];
  shouldAnimate: boolean;
  CanvasVisualizationArea: InteractiveCanvasComponents['CanvasVisualizationArea'];
  isPreviewResultsEnabled: boolean;
  textAccessibleColors: Array<string> | undefined;
}

export function WordCloudPresentation({
  title,
  styledTitle,
  label,
  longerDescription,
  fontSizeOffset,
  shouldHideResults,
  onRemoveWord,
  isModerationEnabled,
  results,
  shouldAnimate,
  CanvasVisualizationArea,
  isPreviewResultsEnabled,
  textAccessibleColors,
}: WordCloudPresentationProps) {
  const formattedResults = useMemo(() => {
    return results.map((item) => ({
      text: item.responseText,
      value: item.responseCount,
    }));
  }, [results]);

  const ConditionalWrapper = CanvasVisualizationArea ?? Box;

  return (
    <Box
      width="100%"
      height="100%"
      data-testid="presentation-container"
      justifyContent="center"
    >
      <TitleSection
        title={title}
        styledTitle={styledTitle}
        description={longerDescription}
        label={label}
        fontSizeOffset={fontSizeOffset}
        showTextSizeDropdown={true}
      />

      <ConditionalWrapper flex="1" width="100%" height="75%">
        <WordCloudVisualization
          key={`${isPreviewResultsEnabled}`} // Force re-render when test votes are toggled
          shouldAnimate={shouldAnimate}
          shouldHideResults={shouldHideResults}
          results={formattedResults}
          onRemoveWord={isModerationEnabled ? onRemoveWord : undefined}
          textAccessibleColors={textAccessibleColors}
        />
      </ConditionalWrapper>
      <ScreenReaderOnly>
        <ScreenReaderResults
          results={formattedResults}
          shouldHideResults={shouldHideResults}
        />
      </ScreenReaderOnly>
    </Box>
  );
}
